export interface TakeProfitStopLossPriceProps {
  pnlRate: number; // 0.1 => 10%
  isTakeProfit: boolean;
  entryPrice: number;
  leverage: number;
  isLong: boolean;
  spreadPercentage: number; // 0.001 => 0.1%
}

/**
 * pnl = collateral * pnlRate
 * size = collateral * fromTokenPrice * leverage
 * priceChangePercentage = pnl * fromTokenPrice / size // since leverup only support fully close
 * priceChangePercentage = pnl * fromTokenPrice / (collateral * fromTokenPrice * leverage)
 *                       = collateral * pnlRate / (collateral * leverage)
 *                       = pnlRate              / leverage
 * TP price = entry price + entry price * priceChangePercentage
 *
 * TP Price = entry Price + entry price * (pnlRate / leverage)
 * SL Price = entry Price - entry price * (pnlRate / leverage)
 * @example
 * collateral = 10
 * pnlRate = 2 // 200%
 * pnl = collateral * 2  = 20
 * leverage = 500 // 500x
 * priceChangePercentage = pnlRate / leverage = 0.004
 * entryPrice = 1000
 * tpPrice = entryPrice * ( 1 + priceChangePercentage )
 *         = entryPrice + entryPrice * priceChangePercentage
 *         = entryPrice + entryPrice * ( pnlRate / leverage )
 *         = 1000 + 1000 * (2/500)
 *         = 1004
 *
 * included spread, for long:
 *
 * (1-spread percentage) * TP trigger price = execution price after spread
 * (1-spread percentage) * SL trigger price = execution price after spread
 *
 * for short
 *
 * (1+spread percentage) * TP trigger price = execution price after spread
 * (1+spread percentage) * SL trigger price = execution price after spread
 *
 */
export const getTakeProfitStopLossPrice = ({
  pnlRate,
  isTakeProfit,
  entryPrice,
  leverage,
  isLong,
  spreadPercentage,
}: TakeProfitStopLossPriceProps) => {
  const priceChange = entryPrice * (pnlRate / leverage);

  if (isLong) {
    return (
      (isTakeProfit ? entryPrice + priceChange : entryPrice - priceChange) /
      (1 - spreadPercentage)
    );
  } else {
    return (
      (isTakeProfit ? entryPrice - priceChange : entryPrice + priceChange) /
      (1 + spreadPercentage)
    );
  }
};
